import React, { useState, useEffect, useRef, useMemo } from 'react';
import api, { services } from '../../../../services/api';
import WebSocketManager from '../../../../websocket/WebSocketManager';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { getExchangeTimezoneSync, exchangeToLocal, getUserTimezone } from '../../../../utils/timezone';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import { 
  FiArrowLeft, FiDownload, FiCalendar, FiClock, FiCheckSquare, 
  FiRefreshCw, FiPlay, FiPause, FiInfo, FiChevronLeft, FiChevronRight,
  FiCheckCircle, FiAlertCircle, FiX, FiShield, FiZap, FiActivity, FiDatabase, FiGlobe
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const DataStatus = {
  FULL: 'full',      
  PARTIAL: 'partial',
  MISSING: 'missing',
  FUTURE: 'future',
  HOLIDAY: 'holiday',
  WEEKEND: 'weekend'
};

const statusColors = {
  [DataStatus.FULL]: 'bg-emerald-500', 
  [DataStatus.PARTIAL]: 'bg-amber-500',
  [DataStatus.MISSING]: 'bg-slate-700 dark:bg-bg-3', // Dot color for missing is dark/neutral
  [DataStatus.FUTURE]: 'bg-transparent',
  [DataStatus.HOLIDAY]: 'bg-rose-500 dark:bg-rose-600',    // Red for holidays
  [DataStatus.WEEKEND]: 'bg-gray-400 dark:bg-bg-3'     // Gray for weekends
};

const HistoricalDataTimeline = ({ instrument, onBack, onCreateSimulation, mode = 'default', onRangeChange, initialRange }) => {
  const { user } = useAuth(); // Get authenticated user from context
  const { showSnackbar } = useSnackbar(); // Get snackbar function from context
  const { isCompact } = useBreakpoint(); // phone/tablet: no hover, so no tooltip
  const [year, setYear] = useState(new Date().getFullYear());
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(initialRange || { start: null, end: null });
  const [continuousFuture, setContinuousFuture] = useState(false);
  // Rollover days: 'default' = use exchange default, or number 1-5 for custom
  const [rolloverDays, setRolloverDays] = useState('default');
  const [exchangeDefaultRollover, setExchangeDefaultRollover] = useState(2); // Fetched from backend
  const [downloads, setDownloads] = useState({}); 
  const [selectedTimeframe, setSelectedTimeframe] = useState('all'); // Default to all timeframes
  // --> Manual expiry override for F&O downloads
  const [expiryOverrideEnabled, setExpiryOverrideEnabled] = useState(false);
  const [expiryOverrideDate, setExpiryOverrideDate] = useState('');
  const [showExchangeTime, setShowExchangeTime] = useState(true); // Default to exchange time
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'calendar'
  // The day whose detail panel is pinned open on touch. The hover tooltip is
  // unreachable without a pointer, so tapping a day also pins its detail below
  // the calendar — same content, same code path, just not hover-gated.
  const [focusedDate, setFocusedDate] = useState(null);

  // Auto-switch view based on screen size
  useEffect(() => {
    const handleResize = () => {
      // Switch to calendar view on smaller screens (< 1280px) where linear timeline is cramped
      if (window.innerWidth < 1280) {
        setViewMode('calendar');
      } else {
        setViewMode('timeline');
      }
    };
    
    // Initial check
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // DeeTrade service modal state (custom modal for detailed information)
  const [deetradeServiceModal, setDeetradeServiceModal] = useState(null);

  // Real calendar data from MDMS backend
  const [calendarData, setCalendarData] = useState({});
  const [rawCalendarData, setRawCalendarData] = useState({}); // Store raw data for tooltips
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  
  // Expiry dates for F&O instruments (from ExpiryPatternService)
  const [expiryDates, setExpiryDates] = useState([]); // List of expiry date strings (YYYY-MM-DD)
  const [expiryMetadata, setExpiryMetadata] = useState({}); // Map: date -> {is_known, month}
  const [expiryPattern, setExpiryPattern] = useState(null); // Pattern description
  
  // Calculate rollover dates based on expiry dates and selected rollover_days
  // Rollover date = N trading days before expiry (skipping weekends AND holidays)
  const rolloverDates = React.useMemo(() => {
    if (!expiryDates.length) return [];
    
    // Get effective rollover days (default to exchange default or 0 if continuous mode off)
    const effectiveDays = continuousFuture 
      ? (rolloverDays === 'default' ? exchangeDefaultRollover : parseInt(rolloverDays, 10))
      : 0;
      
    if (effectiveDays === 0) return []; // No rollover display if 0
    
    const rolloverList = [];
    
    expiryDates.forEach(expiryStr => {
      try {
        // Parse as UTC date
        const expiryDate = new Date(expiryStr);
        let rolloverDate = new Date(expiryDate);
        let daysToSkip = effectiveDays;
        
        // Go back N trading days (skip weekends and holidays)
        // Safety break after 30 iterations to prevent infinite loops if data is weird
        let safetyCounter = 0;
        
        while (daysToSkip > 0 && safetyCounter < 30) {
          // Use UTC date methods to prevent timezone shifts
          rolloverDate.setUTCDate(rolloverDate.getUTCDate() - 1);
          safetyCounter++;
          
          const dayOfWeek = rolloverDate.getUTCDay(); // 0=Sun, 6=Sat in UTC
          const currentDateStr = rolloverDate.toISOString().split('T')[0];
          
          // Check for holiday in calendarData
          // We need to check if ANY timeframe marks it as a holiday
          const status = calendarData[currentDateStr];
          const isHoliday = status === DataStatus.HOLIDAY;
          
          // Skip weekends (0=Sun, 6=Sat) AND holidays
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          if (!isWeekend && !isHoliday) {
            daysToSkip--;
          }
        }
        
        // Format as YYYY-MM-DD
        const rolloverStr = rolloverDate.toISOString().split('T')[0];
        rolloverList.push(rolloverStr);
      } catch (e) {
        console.warn('Failed to calculate rollover date for:', expiryStr, e);
      }
    });
    
    return rolloverList;
  }, [expiryDates, rolloverDays, exchangeDefaultRollover, continuousFuture, calendarData]);
  
  // Dynamic Total Record Count Calculation
  const totalRecordCount = useMemo(() => {
    // If instrument already provides it, use it
    if (instrument.total_records > 0) return instrument.total_records;

    // Otherwise, sum up from rawCalendarData
    if (!rawCalendarData) return 0;
    
    let total = 0;
    Object.values(rawCalendarData).forEach(dayData => {
         // dayData is a map of timeframes -> { status, record_count, ... }
         // To get the most accurate "Total Records" (representing database size/granularity),
         // we should take the count from the highest resolution (maximum records) available for that day.
         // Summing all timeframes would double-count overlapping data.
         // Taking only selectedTimeframe might under-report if user is viewing 1H but 1M data exists.
         
         const counts = Object.values(dayData)
            .map(d => d?.record_count || 0)
            .filter(c => c > 0);
            
         if (counts.length > 0) {
             total += Math.max(...counts);
         }
    });
    return total;
  }, [instrument, rawCalendarData]);

  // Broker selection for historical data download
  const [brokerOptions, setBrokerOptions] = useState(null); // { available_brokers, recommended_broker_id, has_multiple_options }
  const [selectedBrokerId, setSelectedBrokerId] = useState(null); // User's selected broker (null = use recommended)
  const [loadingBrokers, setLoadingBrokers] = useState(false);


  // Fetch real calendar data from MDMS - FETCH ALL TIMEFRAMES ONCE
  useEffect(() => {
    fetchCalendarData();
  }, [year, instrument]); // Removed selectedTimeframe dependency - fetch once, filter locally

  // Fetch expiry dates for F&O instruments
  useEffect(() => {
    const fetchExpiryCalendar = async () => {
      // Check if this is an F&O instrument
      const instType = (instrument?.instrument_type || instrument?.type || '').toUpperCase();
      const isFnO = ['FUTURE', 'OPTION', 'FUT', 'OPT'].includes(instType);
      const isFnOExchange = ['NFO', 'MCX', 'CDS', 'BFO'].includes((instrument?.exchange || '').toUpperCase());
      const hasExpiry = !!instrument?.expiry_date || !!instrument?.expiry;
      
      if (!instrument?.symbol || !(isFnO || (isFnOExchange && hasExpiry))) {
        setExpiryDates([]);
        setExpiryPattern(null);
        return;
      }
      
      try {
        // Fetch from Instrument Master's expiry-calendar endpoint
        const response = await services.instrumentMaster.getExpiryCalendar({
          symbol: instrument.symbol,
          exchange: instrument.exchange,
          year: year,
          instrument_type: instType || 'FUTURE'
        });
        
        if (response?.expiry_dates) {
          // API now returns enriched format: [{date, is_known, month}, ...]
          // Support both old format (array of strings) and new format (array of objects)
          const isNewFormat = typeof response.expiry_dates[0] === 'object';
          
          if (isNewFormat) {
            // New enriched format
            const dates = response.expiry_dates.map(exp => exp.date);
            const metadata = {};
            
            response.expiry_dates.forEach(exp => {
              metadata[exp.date] = {
                is_known: exp.is_known !== undefined ? exp.is_known : true,
                month: exp.month,
                method: exp.method // Capture method for Discovered vs Estimated distinction
              };
            });
            
            setExpiryDates(dates);
            setExpiryMetadata(metadata);
            
            const knownCount = Object.values(metadata).filter(m => m.is_known).length;
            const calculatedCount = dates.length - knownCount;
            console.log(`📅 Loaded ${dates.length} expiry dates for ${instrument.symbol} (${knownCount} known, ${calculatedCount} calculated)`, response.pattern);
          } else {
            // Legacy format - array of date strings
            setExpiryDates(response.expiry_dates);
            setExpiryMetadata({});
            console.log(`📅 Loaded ${response.expiry_dates.length} expiry dates for ${instrument.symbol}`, response.pattern);
          }
          
          setExpiryPattern(response.pattern);
        }
      } catch (error) {
        console.warn('Failed to fetch expiry calendar:', error);
        setExpiryDates([]);
      }
    };
    
    fetchExpiryCalendar();
  }, [instrument, year]);

  // Fetch broker options for historical data download
  useEffect(() => {
    const fetchBrokerOptions = async () => {
      const instrumentId = instrument?.instrument_id || instrument?.id;
      if (!instrumentId) {
        setBrokerOptions(null);
        return;
      }

      setLoadingBrokers(true);
      const MAX_RETRIES = 2;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const response = await services.marketData.getBrokerOptions(instrumentId);
          const data = response.data || response;
          setBrokerOptions(data);
          setSelectedBrokerId(data.recommended_broker_id || null);
          console.log('📊 Broker options loaded:', data);
          setLoadingBrokers(false);
          return; // success — exit retry loop
        } catch (error) {
          console.warn(`Failed to fetch broker options (attempt ${attempt + 1}/${MAX_RETRIES + 1}):`, error);
          if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 1500)); // wait before retry
          }
        }
      }
      // all retries exhausted
      setBrokerOptions(null);
      setLoadingBrokers(false);
    };

    fetchBrokerOptions();
  }, [instrument]);

  // Fetch exchange default rollover days when instrument changes
  useEffect(() => {
    const fetchRolloverDefault = async () => {
      const exchange = (instrument?.exchange || '').toUpperCase();
      if (!exchange) return;
      
      try {
        // Fetch exchange-specific rollover default from MDMS
        const response = await services.marketData.getRolloverDefault(exchange);
        const defaultDays = response?.default_days || response?.data?.default_days || 2;
        setExchangeDefaultRollover(defaultDays);
        console.log(`📅 Exchange ${exchange} default rollover: ${defaultDays} days`);
      } catch (error) {
        console.warn('Failed to fetch rollover default, using fallback:', error);
        // Fallback defaults based on exchange
        const fallbackDefaults = {
          'MCX': 3,
          'NFO': 2,
          'BFO': 2,
          'CDS': 2,
          'NSE': 2,
          'BSE': 2
        };
        setExchangeDefaultRollover(fallbackDefaults[exchange] || 2);
      }
    };
    
    fetchRolloverDefault();
  }, [instrument?.exchange]);

  // Subscribe to real-time availability updates

  useEffect(() => {
    if (!instrument || !instrument.instrument_id || !selectedTimeframe) {
      return;
    }

    // Subscribe to key availability streams so calendar can update without refresh.
    const topics = Array.from(new Set([
      `availability.${instrument.instrument_id}.1d`,
      `availability.${instrument.instrument_id}.1m`,
      `availability.${instrument.instrument_id}.1s`,
      `availability.${instrument.instrument_id}.${selectedTimeframe}`
    ]));
    console.log(`📡 Subscribing to availability updates: ${topics.join(', ')}`);

    const handleAvailabilityUpdate = (data) => {
      console.log('📅 Received availability update:', data);
      
      // New format uses calendar_data (matching API response)
      // Also support legacy format with dates array
      const calendarDataFromWS = data.calendar_data;
      const datesArray = calendarDataFromWS ? Object.keys(calendarDataFromWS) : data.dates;
      
      if (datesArray && data.year === year) {
        // First, update rawCalendarData so changes persist when switching timeframes
        setRawCalendarData(prev => {
          const updated = { ...prev };
          const tf = data.timeframe || selectedTimeframe;
          
          if (calendarDataFromWS) {
            // New format: calendar_data has per-date objects with record_count, status, etc.
            Object.entries(calendarDataFromWS).forEach(([dateStr, dayData]) => {
              if (!updated[dateStr]) {
                updated[dateStr] = {};
              }
              // Merge the day data for this timeframe (same format as API)
              updated[dateStr][tf] = {
                ...dayData,
                updated_via: 'websocket'
              };
            });
          } else {
            // Legacy format: dates array with date_record_counts
            datesArray.forEach(dateStr => {
              if (!updated[dateStr]) {
                updated[dateStr] = {};
              }
              const recordCount = data.date_record_counts?.[dateStr] || data.total_records || 1;
              const percentage = recordCount > 0 ? 100 : 0;
              
              updated[dateStr][tf] = {
                date: dateStr,
                status: 'complete',
                record_count: recordCount,
                completeness_percentage: percentage,
                updated_via: 'websocket'
              };
            });
          }
          
          // Always derive calendar view from merged raw data to avoid stale/partial UI until refresh.
          setTimeout(() => filterCalendarByTimeframe(updated, selectedTimeframe), 0);
          return updated;
        });
        
        // Log summary
        const totalRecords = calendarDataFromWS 
          ? Object.values(calendarDataFromWS).reduce((sum, d) => sum + (d.record_count || 0), 0)
          : data.total_records || datesArray.length;
        console.log(`✅ Calendar updated with ${datesArray.length} dates, ${totalRecords} records (persisted to rawCalendarData)`);
      }
    };

    // Subscribe to WebSocket topics and keep all unsubscribe handles.
    const unsubscribers = topics.map((topic) => WebSocketManager.subscribeTopic(topic, handleAvailabilityUpdate));

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log(`📡 Unsubscribing from availability updates: ${topics.join(', ')}`);
      unsubscribers.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [instrument, selectedTimeframe, year]);

  const fetchCalendarData = async () => {
    if (!instrument || !instrument.instrument_id) {
      console.warn('No instrument_id available, skipping calendar fetch');
      return;
    }

    try {
      setLoadingCalendar(true);
      setCalendarError(null);

      // STRATEGY: 
      // 1. Fetch '1d' immediately for high-level "All" view (Priority 1)
      // 2. Then fetch '1m' and '1s' (Priority 2)
      // 3. Skip '5m', '15m', '1h' - use '1m' data as proxy for these
      
      // Step 1: Fetch 1d with instrument metadata for empty instruments
      const res1d = await services.marketData.getYearlyCalendar(instrument.instrument_id, year, '1d', instrument);
      
      const initialRawData = {};
      if (res1d.data?.calendar_data) {
          Object.entries(res1d.data.calendar_data).forEach(([dateStr, dayData]) => {
              if (!initialRawData[dateStr]) initialRawData[dateStr] = {};
              initialRawData[dateStr]['1d'] = dayData;
          });
      }
      
      // Update UI with 1d data immediately
      setRawCalendarData(initialRawData);
      filterCalendarByTimeframe(initialRawData, selectedTimeframe);
      setLoadingCalendar(false); // Enable interaction while background fetch continues
      
      // Step 2: Background Fetch 1m and 1s
      // Only fetch if we are not destroyed/unmounted (check handled by react state update safety usually, but here we just proceed)
      // We don't await this block for the "loading" state if we want UI to be responsive, 
      // but usually we want to know when fully done. 
      // User likely wants to see 1d immediately.
      
      const granularTimeframes = ['1m', '1s'];
      const promises = granularTimeframes.map(tf => 
        services.marketData.getYearlyCalendar(instrument.instrument_id, year, tf, instrument)
      );
      
      const results = await Promise.all(promises);
      
      setRawCalendarData(prevData => {
          const updated = { ...prevData };
          
          results.forEach((response, idx) => {
            const backendCalendar = response.data?.calendar_data || {};
            const tf = granularTimeframes[idx];
            
            Object.entries(backendCalendar).forEach(([dateStr, dayData]) => {
              if (!updated[dateStr]) updated[dateStr] = {};
              updated[dateStr][tf] = dayData;
            });
          });
          
          // Re-trigger filter with new complete data
          setTimeout(() => filterCalendarByTimeframe(updated, selectedTimeframe), 0);
          return updated;
      });
      
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setCalendarError('Failed to load calendar data');
      // Don't wipe data if partial load succeeded
    } finally {
      setLoadingCalendar(false);
    }
  };

  // Local filtering function - called when selectedTimeframe changes
  const filterCalendarByTimeframe = (rawData, timeframe) => {
    const filtered = {};
    
    if (timeframe === 'all') {
      // Show date as complete if ANY timeframe has data
      Object.entries(rawData).forEach(([dateStr, timeframeData]) => {
        // Check for holiday/weekend status from any timeframe data
        const firstData = Object.values(timeframeData)[0];
        const isHoliday = firstData?.is_holiday || firstData?.status === 'holiday';
        const isWeekend = firstData?.is_weekend || firstData?.status === 'weekend';
        const isFuture = new Date(dateStr) > new Date();

        // Priority: Holiday > Weekend > Future > Data Status
        // This ensures holidays are preserved even if they are in the future
        if (isHoliday) {
          filtered[dateStr] = DataStatus.HOLIDAY;
        } else if (isWeekend) {
          filtered[dateStr] = DataStatus.WEEKEND;
        } else if (isFuture) {
          filtered[dateStr] = DataStatus.FUTURE;
        } else {
          const hasAnyData = Object.values(timeframeData).some(
            dayData => dayData?.status === 'complete' || dayData?.status === 'partial'
          );
          
          if (hasAnyData) {
            const isFullyComplete = Object.values(timeframeData).some(
              dayData => dayData?.status === 'complete'
            );
            filtered[dateStr] = isFullyComplete ? DataStatus.FULL : DataStatus.PARTIAL;
          } else {
            filtered[dateStr] = DataStatus.MISSING;
          }
        }
      });
    } else {
      // Show data for specific timeframe only
      Object.entries(rawData).forEach(([dateStr, timeframeData]) => {
        let dayData = timeframeData[timeframe];
        
        // PROXY LOGIC: Use '1m' availability as proxy for '5m', '15m', '1h'
        // Since we only fetch 1d, 1m, 1s now.
        if (!dayData && ['5m', '15m', '1h', '30m'].includes(timeframe)) {
           dayData = timeframeData['1m'];
        }
        const isFuture = new Date(dateStr) > new Date();
        
        if (dayData) {
          const backendStatus = dayData.status || 'missing';
          const isHoliday = dayData.is_holiday || backendStatus === 'holiday';
          const isWeekend = dayData.is_weekend || backendStatus === 'weekend';
          
          if (isHoliday) {
            filtered[dateStr] = DataStatus.HOLIDAY;
          } else if (isWeekend) {
            filtered[dateStr] = DataStatus.WEEKEND;
          } else if (isFuture) {
            filtered[dateStr] = DataStatus.FUTURE;
          } else if (backendStatus === 'complete') {
            filtered[dateStr] = DataStatus.FULL;
          } else if (backendStatus === 'partial') {
            filtered[dateStr] = DataStatus.PARTIAL;
          } else {
            filtered[dateStr] = DataStatus.MISSING;
          }
        } else {
           if (isFuture) {
              filtered[dateStr] = DataStatus.FUTURE;
           } else {
              filtered[dateStr] = DataStatus.MISSING;
           }
        }
      });
    }
    
    setCalendarData(filtered);
    console.log(`Calendar filtered for ${timeframe}:`, {
      totalDays: Object.keys(filtered).length,
      timeframe
    });
  };

  // Re-filter when selectedTimeframe changes (NO API call, just local filtering)
  useEffect(() => {
    if (Object.keys(rawCalendarData).length > 0) {
      filterCalendarByTimeframe(rawCalendarData, selectedTimeframe);
    }
  }, [selectedTimeframe]); // Only dependency is selectedTimeframe

  const toggleDaySelection = (dateStr) => {
      let newRange = { ...selectedRange };
      
      if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
          newRange = { start: dateStr, end: null };
      } else {
          if (dateStr > selectedRange.start) newRange = { ...selectedRange, end: dateStr };
          else newRange = { start: dateStr, end: selectedRange.start };
      }
      
      setSelectedRange(newRange);
      if (onRangeChange) {
         onRangeChange(newRange);
      }
  };

  const startDownload = () => {
      // Prepare payload with user from auth context
      if (!user) {
          console.error('User not authenticated; cannot request historical data');
          showSnackbar('Please log in to request historical data', 'error');
          return;
      }

      // Ensure at least one date is selected
      if (!selectedRange.start) {
          showSnackbar('Please select a date range first', 'info');
          return;
      }

      // CRITICAL: Generate job_id on frontend and subscribe BEFORE API call
      // This prevents race condition where backend publishes progress before frontend subscribes
      const jobId = `${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const topic = `historical-download.${jobId}`;
      
      console.log(`🔔 Pre-subscribing to ${topic} BEFORE API call`);
      
      // Define progress handler
      const handleProgress = (data) => {
          console.log('📊 Historical download progress:', data);
          setDownloads(prev => {
              if (!prev[jobId]) return prev;
            const existing = prev[jobId];
            const stage = data.stage || existing.stage || 'starting';
            const explicitStatus = data.status;

            // Stage-first status resolution: avoid sticky 100% if an old/completed status leaks in.
            let status = explicitStatus || 'in_progress';
            const terminalStatuses = new Set(['completed', 'failed', 'partial']);
            const terminalStages = new Set(['completed', 'failed']);

            if (!explicitStatus) {
              if (terminalStages.has(stage)) {
                status = stage === 'failed' ? 'failed' : 'completed';
              } else {
                status = 'in_progress';
              }
            }

            let downloadProgress = existing.downloadProgress ?? 0;
            let insertProgress = existing.insertProgress ?? 0;
            let downloadCompletedChunks = existing.downloadCompletedChunks ?? 0;
            let downloadTotalChunks = existing.downloadTotalChunks ?? 0;

            const currentCount = Number(data.current_count ?? 0);
            const totalCount = Number(data.total_count ?? 0);
            const insertedRecords = Number(data.inserted_records ?? existing.insertedRecords ?? 0);
            const downloadedRecords = Number(data.downloaded_records ?? existing.downloadedRecords ?? 0);
            const chunkRecordsDownloaded = Number(data.chunk_records_downloaded ?? existing.chunkRecordsDownloaded ?? 0);
            const noDataChunks = Number(data.no_data_chunks ?? existing.noDataChunks ?? 0);
            const errorChunks = Number(data.error_chunks ?? existing.errorChunks ?? 0);
            const insertChunkIndex = Number(data.insert_chunk_index ?? existing.insertChunkIndex ?? 0);
            const insertTotalChunks = Number(data.insert_total_chunks ?? existing.insertTotalChunks ?? 0);

            const existingDiscovery = existing.discovery || {};
            const nextDiscovery = {
              generatedCandidates: existingDiscovery.generatedCandidates || [],
              attempts: existingDiscovery.attempts || [],
              selectedExpiry: existingDiscovery.selectedExpiry || null,
              candidatesTried: existingDiscovery.candidatesTried || [],
              monthKey: existingDiscovery.monthKey || null,
            };

            if (data.discovery_evaluation) {
              const evt = data.discovery_evaluation;
              const monthKey = evt.year && evt.month ? `${evt.year}-${String(evt.month).padStart(2, '0')}` : nextDiscovery.monthKey;
              nextDiscovery.monthKey = monthKey;

              if (evt.type === 'generated' && Array.isArray(evt.candidate_dates)) {
                nextDiscovery.generatedCandidates = evt.candidate_dates;
              }

              if (evt.type === 'probe' && evt.candidate) {
                const attemptLabel = `${evt.candidate} → ${evt.result || 'unknown'}`;
                nextDiscovery.attempts = [...nextDiscovery.attempts, attemptLabel].slice(-12);
                if (evt.result === 'valid') {
                  nextDiscovery.selectedExpiry = evt.candidate;
                }
              }
            }

            if (data.discovered_expiry) {
              const de = data.discovered_expiry;
              if (Array.isArray(de.candidate_dates) && de.candidate_dates.length > 0) {
                nextDiscovery.generatedCandidates = de.candidate_dates;
              }
              if (Array.isArray(de.candidates_tried) && de.candidates_tried.length > 0) {
                nextDiscovery.candidatesTried = de.candidates_tried;
              }
              if (de.expiry_date) {
                nextDiscovery.selectedExpiry = de.expiry_date;
              }
            }

            // Download progress should be driven by completed/total download chunks.
            if (stage === 'downloading') {
              if (totalCount > 0) {
                downloadTotalChunks = Math.max(downloadTotalChunks, totalCount);
              }
              if (currentCount >= 0) {
                downloadCompletedChunks = Math.max(downloadCompletedChunks, currentCount);
              }
              if (downloadTotalChunks > 0) {
                const computedDownload = Math.min(99, (downloadCompletedChunks / downloadTotalChunks) * 100);
                downloadProgress = Math.max(downloadProgress, computedDownload);
              }
            }

            // Download becomes complete after broker-download loop finishes normalizing.
            if (stage === 'normalizing') {
              downloadProgress = Math.max(downloadProgress, 100);
              if (downloadTotalChunks > 0) {
                downloadCompletedChunks = Math.max(downloadCompletedChunks, downloadTotalChunks);
              }
            }

            // Confirmed progress is inserted share of buffered(downloaded) progress.
            if (downloadedRecords > 0) {
              const insertedShare = Math.min(1, insertedRecords / downloadedRecords);
              const confirmedFromBuffered = downloadProgress * insertedShare;
              insertProgress = Math.max(insertProgress, confirmedFromBuffered);
            }

            // Weighted overall progress: download phase (80%) + insert phase (20%).
            let overallProgress = (downloadProgress * 0.8) + (insertProgress * 0.2);

            if (status === 'completed' && (explicitStatus === 'completed' || stage === 'completed')) {
              overallProgress = 100;
              downloadProgress = 100;
              insertProgress = 100;
              if (downloadTotalChunks > 0) {
                downloadCompletedChunks = downloadTotalChunks;
              }
            }

            if (status === 'partial') {
              overallProgress = Math.max(overallProgress, 90);
            }

            if (status === 'failed') {
              overallProgress = Math.max(overallProgress, 1);
            }

            // If previously terminal but current event is non-terminal, unstick from terminal UI.
            if (terminalStatuses.has(existing.status) && !terminalStatuses.has(status)) {
              overallProgress = Math.min(overallProgress, 99.5);
            }
              
              return {
                  ...prev,
                  [jobId]: {
                ...existing,
                stage,
                status,
                overallProgress,
                downloadProgress,
                insertProgress,
                downloadCompletedChunks,
                downloadTotalChunks,
                insertChunkIndex,
                insertTotalChunks,
                insertedRecords,
                downloadedRecords,
                chunkRecordsDownloaded,
                noDataChunks,
                errorChunks,
                discovery: nextDiscovery,
                      message: data.message,
                      errors: data.errors
                  }
              };
          });
          
            // Handle resolved expiry updates from backend (verified or estimated)
          if (data.discovered_expiry) {
              console.log('🎯 Resolved expiry update:', data.discovered_expiry);
              const { expiry_date, method, confidence } = data.discovered_expiry;
              
              // Guard: Don't let an unvalidated broadcast downgrade a previously validated discovery.
              // This prevents transient API failures during download from overwriting a correct
              // discovery_validated expiry with a fallback smart_discovery estimate.
              const VALIDATED_METHODS = ['discovery_validated', 'discovery_persisted', 'exact_match'];
              const newDate = new Date(expiry_date);
              const newMonth = newDate.getMonth();
              const newYear = newDate.getFullYear();
              
              // Check if we already have a validated entry for this month
              const existingForMonth = Object.entries(expiryMetadata).find(([dateStr, meta]) => {
                  const d = new Date(dateStr);
                  return d.getMonth() === newMonth && d.getFullYear() === newYear 
                      && VALIDATED_METHODS.includes(meta?.method);
              });
              
              if (existingForMonth && !VALIDATED_METHODS.includes(method)) {
                  // Existing validated entry is more trustworthy — skip this broadcast
                  console.log(`⚠️ Ignoring ${method} broadcast for ${expiry_date} — already have ${existingForMonth[1].method} at ${existingForMonth[0]}`);
              } else {
                  // Update expiry dates — remove stale entry for same month/year, add new one
                  setExpiryDates(prev => {
                      const filtered = prev.filter(d => {
                          const dDate = new Date(d);
                          return !(dDate.getMonth() === newMonth && dDate.getFullYear() === newYear);
                      });
                      return [...filtered, expiry_date].sort();
                  });
                  
                  setExpiryMetadata(prev => ({
                      ...prev,
                      [expiry_date]: {
                          is_known: false,
                          confidence: confidence || 0.95,
                          method: method || 'smart_discovery',
                          month: newDate.getMonth() + 1
                      }
                  }));
                  
                    const isValidated = VALIDATED_METHODS.includes(method);
                    if (isValidated) {
                      showSnackbar(`Verified expiry resolved: ${expiry_date} (${method})`, 'success', 3000);
                    } else {
                      showSnackbar(`Estimated expiry candidate: ${expiry_date} (${method})`, 'info', 3000);
                    }
              }
          }
          
          if (data.status === 'failed' && data.message) {
              showSnackbar(data.message, 'error', 8000);
          }

          // --> Clean up terminal downloads after delay (allows WS availability update to refresh calendar first)
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'partial') {
              const cleanupDelay = data.status === 'failed' ? 20000 : 30000;
              setTimeout(() => {
                  setDownloads(prev => {
                      const updated = { ...prev };
                      delete updated[jobId];
                      return updated;
                  });
              }, cleanupDelay);
          }
          
            // Unsubscribe only on explicit terminal events from backend payload.
            if (data.status === 'completed' || data.status === 'failed' || data.status === 'partial') {
              setTimeout(() => {
                  WebSocketManager.unsubscribeTopic(topic, handleProgress);
              }, 5000);
          }
      };
      
      // Subscribe BEFORE API call
      WebSocketManager.subscribeTopic(topic, handleProgress);
      
      // Initialize download state
      setDownloads(prev => ({
          ...prev,
          [jobId]: {
              overallProgress: 0,
              downloadProgress: 0,
              insertProgress: 0,
              downloadCompletedChunks: 0,
              downloadTotalChunks: 0,
              insertChunkIndex: 0,
              insertTotalChunks: 0,
              insertedRecords: 0,
              downloadedRecords: 0,
              chunkRecordsDownloaded: 0,
              noDataChunks: 0,
              errorChunks: 0,
              discovery: {
                generatedCandidates: [],
                attempts: [],
                selectedExpiry: null,
                candidatesTried: [],
                monthKey: null,
              },
              status: 'starting',
              stage: 'initializing',
              range: selectedRange,
              type: continuousFuture ? 'Continuous' : 'Standard',
              broker: 'Preparing...',
              message: 'Initializing download...'
          }
      }));

      const payload = {
          job_id: jobId,  // Send frontend-generated job_id to backend
          instrument: {
              instrument_id: instrument.instrument_id,
              symbol: instrument.symbol,
              exchange: instrument.exchange,
              // Include enrichment fields from instrument master (if available)
              composite_key: instrument.composite_key || null,
              exchange_token: instrument.exchange_token || null,
              instrument_type: instrument.instrument_type || instrument.type || null,
              // Broker selection fields from Instrument Master
              primary_broker_id: instrument.primary_broker_id || null,
              last_updated_by_broker_id: instrument.last_updated_by_broker_id || null,
              broker_data_sources: instrument.broker_data_sources || null,  // Fallback data
              // F&O fields - critical for futures/options historical data
              expiry_date: instrument.expiry_date || instrument.expiry || null,
              strike_price: instrument.strike_price || instrument.strike || null,
              option_type: instrument.option_type || null,
          },
          start_date: selectedRange.start,
          end_date: selectedRange.end || selectedRange.start, // Default to start_date if only one day selected
          timeframe: selectedTimeframe === 'all' ? '1d' : selectedTimeframe, // Send selected interval, default '1d' for 'all'
          options: { 
              continuous: continuousFuture,
              // Send actual rollover days value (default or custom)
              rollover_days: rolloverDays === 'default' ? exchangeDefaultRollover : parseInt(rolloverDays, 10),
              // Manual override: user-specified contract end date (YYYY-MM-DD or null)
              override_expiry_date: expiryOverrideEnabled && expiryOverrideDate ? expiryOverrideDate : null
          },
          user: { 
              id: user.id || user.user_id || null, 
              email: user.email || user.username || null 
          },
          // User's broker selection (if different from recommended)
          preferred_broker_id: selectedBrokerId || null,
      };

      // Send request to MDMS
      services.marketData.requestHistorical(payload)
        .then((mdmsResp) => {
            console.log('MDMS historical request response:', mdmsResp.data || mdmsResp);
            const response = mdmsResp.data || mdmsResp;
            
            // Handle different response statuses
            if (response.status === 'error') {
                // Backend returned an error response - display to user
                const errorMessage = response.message || response.details || 'Failed to process historical data request';
                showSnackbar(errorMessage, 'error');
                return; // Exit early to prevent further processing
            } else if (response.status === 'request_submitted') {
                // User's brokers don't support historical data - DeeTrade will facilitate
                setDeetradeServiceModal({
                    brokers: response.broker_info?.user_brokers || [],
                    message: response.deetrade_service?.message || "DeeTrade will facilitate this download for you!",
                    details: response.deetrade_service?.details || "We'll notify you when the data is ready.",
                    requestId: response.deetrade_service?.request_id,
                    estimatedTime: response.deetrade_service?.estimated_time || "24-48 hours",
                    requestDetails: response.request_details
                });
            } else if (response.status === 'duplicate') {
                // Duplicate request - use existing snackbar system
                showSnackbar(response.message, 'info');
            } else if (response.status === 'ready_to_download' || response.status === 'download_initiated') {
                // Broker supports historical data - update download state with broker info
                showSnackbar('Download started! Real-time progress will be displayed below.', 'success');
                
                // Update download state with broker information (subscription already active)
                setDownloads(prev => ({
                    ...prev,
                    [jobId]: {
                        ...prev[jobId],
                        broker: response.broker || response.broker_validation?.recommended_broker || 'ICICI',
                        status: 'downloading'
                    }
                }));
            } else {
                // Generic success - use existing snackbar system
                showSnackbar('Historical data request submitted successfully!', 'success');
            }
        })
        .catch((err) => {
            console.error('MDMS request failed:', err);
            
            // cleanup subscription on error
            WebSocketManager.unsubscribeTopic(topic, handleProgress);
            
            // Remove the stuck "Starting..." state
             setDownloads(prev => {
                const updated = { ...prev };
                delete updated[jobId];
                return updated;
            });

            // Extract error message from response if available
            let errorMessage = 'Failed to submit request. Please try again.';
            if (err.response && err.response.data) {
                const errorData = err.response.data;
                // Check for nested broker adapter error
                if (errorData.message && errorData.message.includes('Broker adapter failed')) {
                    try {
                        // Parse the nested JSON error message
                        const match = errorData.message.match(/Broker adapter failed: ({.*})/);
                        if (match && match[1]) {
                            const nestedError = JSON.parse(match[1]);
                            if (nestedError.detail) {
                                errorMessage = nestedError.detail;
                            }
                        } else {
                            errorMessage = errorData.message;
                        }
                    } catch (e) {
                        errorMessage = errorData.message;
                    }
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            showSnackbar(errorMessage, 'error');
        });

      setDownloadModalOpen(false);
      setSelectedRange({ start: null, end: null });
      // Reset override state for next download
      setExpiryOverrideEnabled(false);
      setExpiryOverrideDate('');
  };

  const isSelected = (dateStr) => {
    if (!selectedRange.start) return false;
    if (!selectedRange.end) return dateStr === selectedRange.start;
    return dateStr >= selectedRange.start && dateStr <= selectedRange.end;
  };

  const changeYear = (direction) => {
      setYear(prev => direction === 'next' ? prev + 1 : prev - 1);
  };

  /* Check if a date falls within any active download range and return progress */
  const getDownloadProgressForDate = (dateStr) => {
    for (const [, task] of Object.entries(downloads)) {
      if (!task.range?.start) continue;
      const start = task.range.start;
      const end = task.range.end || start;
      if (dateStr >= start && dateStr <= end) {
        // --> Show arc for all statuses including terminal ones until calendar data refreshes
        const progress = (task.status === 'completed' || task.status === 'partial' || task.status === 'failed')
          ? 100
          : task.overallProgress || 0;
        return { active: true, progress, status: task.status };
      }
    }
    return { active: false, progress: 0, status: null };
  };

  /* Everything the day tooltip / detail panel needs, derived from the date alone.
     Extracted so the hover tooltip and the touch detail panel read identical facts. */
  const getDayFacts = (dateStr) => {
    const status = calendarData[dateStr] || DataStatus.MISSING;
    const meta = expiryMetadata[dateStr];
    const isKnownContract = meta?.is_known !== false;
    return {
      status,
      indicatorColor: statusColors[status],
      isExpiryDate: expiryDates.includes(dateStr),
      isKnownContract,
      isDiscovered: !isKnownContract && (
        meta?.method === 'discovery_validated' ||
        meta?.method === 'discovery_persisted'
      ),
      isRolloverDate: rolloverDates.includes(dateStr),
    };
  };

  /* The body of a day's detail — expiry/rollover context, holiday name, per-timeframe
     completeness and market hours. Rendered into the hover tooltip on desktop and into
     the pinned panel on touch, so the two can never drift.
     NOTE: written for a DARK ground (the tooltip surface). Both callers must supply
     `bg-gray-900 dark:bg-bg-2`, or the light text here becomes invisible. */
  const renderDayDetailBody = (dateStr) => {
    const { status, indicatorColor, isExpiryDate, isKnownContract, isDiscovered, isRolloverDate } = getDayFacts(dateStr);

    return (
      <>
        {isExpiryDate && (
          <div className={`flex items-center gap-2 mb-1.5 pb-1.5 border-b ${
              isDiscovered ? 'border-purple-500/30' : 'border-amber-500/30'
          }`}>
            <span className={`text-white text-[8px] px-1.5 py-0.5 rounded font-bold ${
              isDiscovered ? 'bg-purple-500' : isKnownContract ? 'bg-emerald-500' : 'bg-amber-500'
            }`}>
              {isDiscovered ? 'DISCOVERED' : isKnownContract ? 'CONTRACT EXPIRY' : 'ESTIMATED'}
            </span>
            <span className={isDiscovered ? 'text-purple-400' : 'text-amber-400'}>
              {isDiscovered
                  ? 'Verified via Discovery'
                  : isKnownContract
                    ? 'Actual Contract Expiry'
                    : (expiryPattern?.description || 'Calculated Expiry')}
            </span>
          </div>
        )}

        {isRolloverDate && !isExpiryDate && (() => {
          const effectiveDays = rolloverDays === 'default'
            ? exchangeDefaultRollover
            : parseInt(rolloverDays, 10);
          return (
            <div className="flex items-center gap-2 mb-1.5 pb-1.5 border-b border-blue-500/30">
              <span className="text-white text-[8px] px-1.5 py-0.5 rounded font-bold bg-blue-500">ROLLOVER DAY</span>
              <span className="text-blue-400">Switch to next contract ({effectiveDays}d before expiry)</span>
            </div>
          );
        })()}

        {(status === DataStatus.HOLIDAY || status === DataStatus.WEEKEND) && (
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-2 h-2 rounded-full ${indicatorColor}`}></div>
            <span className={status === DataStatus.HOLIDAY ? 'text-rose-400' : 'text-white/60'}>
              {status === DataStatus.HOLIDAY ? '🏖️ ' : ''}
              {(() => {
                const dayData = rawCalendarData[dateStr];
                if (dayData) {
                  const firstTf = Object.values(dayData)[0];
                  if (firstTf?.holiday_name) return firstTf.holiday_name;
                }
                return status === DataStatus.HOLIDAY ? 'Market Holiday' : 'Weekend - Markets Closed';
              })()}
            </span>
          </div>
        )}

        {status !== DataStatus.HOLIDAY && status !== DataStatus.WEEKEND && (
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-2 h-2 rounded-full ${indicatorColor}`}></div>
            <span className="capitalize">{status} Data</span>
          </div>
        )}

        {rawCalendarData[dateStr] && status !== DataStatus.HOLIDAY && status !== DataStatus.WEEKEND && (
          <div className="text-white/60 mt-1 space-y-0.5">
            {Object.entries(rawCalendarData[dateStr]).map(([tf, data]) => {
              const percentage = data?.completeness_percentage ?? (data?.status === 'complete' ? 100 : 0);
              const recordCount = data?.record_count ?? 0;
              return (
                <div key={tf} className="flex justify-between gap-3">
                  <span className="font-mono">{tf.toUpperCase()}:</span>
                  <span className={`font-semibold ${
                    percentage >= 95 ? 'text-emerald-400' :
                    percentage > 0 ? 'text-amber-400' :
                    'text-white/70'
                  }`}>
                    {percentage.toFixed(0)}% ({recordCount} records)
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {rawCalendarData[dateStr] && status !== DataStatus.HOLIDAY && status !== DataStatus.WEEKEND && (
          (() => {
            const dayData = Object.values(rawCalendarData[dateStr])[0];
            if (dayData?.market_open && dayData?.market_close) {
              const exchangeTz = getExchangeTimezoneSync(instrument.exchange);
              const tzLabel = showExchangeTime
                ? exchangeTz.split('/').pop()
                : getUserTimezone().split('/').pop().replace('_', ' ');
              const displayOpen = showExchangeTime
                ? dayData.market_open
                : exchangeToLocal(dayData.market_open, exchangeTz, { hour: '2-digit', minute: '2-digit', hour12: true });
              const displayClose = showExchangeTime
                ? dayData.market_close
                : exchangeToLocal(dayData.market_close, exchangeTz, { hour: '2-digit', minute: '2-digit', hour12: true });
              return (
                <div className="text-white/80 mt-1.5 pt-1.5 border-t border-white/15">
                  <div className="flex items-center gap-1">
                    🕐 <span>{displayOpen} - {displayClose}</span>
                    <span className="text-[8px] text-white/60 ml-1">({tzLabel})</span>
                  </div>
                </div>
              );
            }
            return null;
          })()
        )}

        {!rawCalendarData[dateStr] && status !== DataStatus.HOLIDAY && status !== DataStatus.WEEKEND && (
          <div className="text-white/70 mt-1">No data available</div>
        )}
      </>
    );
  };

  /* Shared Day Slot Renderer to ensure identical logic/tooltips across views */
  const renderDaySlot = (day, idx, mIdx, isTopRow, layoutClasses) => {
    if (!day) return <div key={idx} className={layoutClasses.empty} />;

    const dateStr = `${year}-${String(mIdx+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    const selected = isSelected(dateStr);

    const { status, indicatorColor, isExpiryDate, isKnownContract, isDiscovered, isRolloverDate } = getDayFacts(dateStr);

    // Weekend faint highlight
    const isWeekend = (idx % 7) >= 5;

    // Check if this date has an active download in progress
    const downloadProgress = getDownloadProgressForDate(dateStr);

    return (
        <div
           key={idx}
           onClick={() => {
             if (status === DataStatus.FUTURE) return;
             toggleDaySelection(dateStr);
             // Touch has no hover, so the tap also pins this day's detail below.
             if (isCompact) setFocusedDate(dateStr);
           }}
           className={`
               group/cell flex flex-col items-center justify-center relative cursor-pointer transition-all rounded 
               ${layoutClasses.cell}
               ${selected 
                   ? 'bg-indigo-600 shadow-md shadow-indigo-300 dark:shadow-none z-10' // High visibility selected bg
                   : 'hover:bg-bg-3 dark:hover:bg-white/5'
               }
               ${!selected && isWeekend ? 'bg-bg-3 dark:bg-white/[0.03]' : ''}
               ${status === DataStatus.FUTURE ? 'opacity-30 pointer-events-none' : ''}
               ${isExpiryDate && !selected 
                   ? (isDiscovered 
                       ? 'ring-1 ring-purple-400 dark:ring-purple-500/60 ring-inset'
                       : isKnownContract 
                           ? 'ring-1 ring-emerald-400 dark:ring-emerald-500/60 ring-inset'
                           : 'ring-1 ring-amber-400 dark:ring-amber-600/60 ring-inset'
                     )
                   : ''
               }
               ${isRolloverDate && !isExpiryDate && !selected ? 'ring-1 ring-blue-400 dark:ring-blue-500/60 ring-inset' : ''}
           `}
        >
            <span className={`text-[10px] font-bold ${
                selected ? 'text-white' 
                : isExpiryDate 
                    ? (isDiscovered 
                        ? 'text-purple-600 dark:text-purple-400' 
                        : isKnownContract 
                            ? 'text-emerald-600 dark:text-emerald-500' 
                            : 'text-amber-600 dark:text-amber-500/80')
                : isRolloverDate 
                    ? 'text-blue-600 dark:text-blue-400' 
                : 'text-fg-2'
            }`}>
                {day}
            </span>
            
            {/* Status Indicator — Donut ring when downloading, dot/dash otherwise */}
            {downloadProgress.active && !selected ? (
                <svg className="mt-0.5 w-3.5 h-3.5" viewBox="0 0 20 20">
                    {/* Background ring */}
                    <circle cx="10" cy="10" r="8" fill="none" strokeWidth="2"
                        className="text-gray-200 dark:text-fg-2" stroke="currentColor" />
                    {/* Progress arc — color reflects terminal status */}
                    <circle cx="10" cy="10" r="8" fill="none" strokeWidth="2.5"
                        className={
                            downloadProgress.status === 'completed' ? 'text-emerald-500' :
                            downloadProgress.status === 'partial'   ? 'text-amber-500'   :
                            downloadProgress.status === 'failed'    ? 'text-red-500'     :
                            'text-indigo-500'
                        }
                        stroke="currentColor"
                        strokeDasharray={`${(downloadProgress.progress / 100) * 50.27} 50.27`}
                        strokeLinecap="round"
                        transform="rotate(-90 10 10)"
                        style={{ transition: 'stroke-dasharray 0.5s ease' }} />
                </svg>
            ) : (
                <>
                    {status !== DataStatus.FUTURE && status !== DataStatus.MISSING && (
                        <div className={`mt-1.5 w-1 h-1 rounded-full ${selected ? 'bg-white' : indicatorColor}`}></div>
                    )}
                    {status === DataStatus.MISSING && (
                        <div className={`mt-1.5 w-1.5 h-0.5 rounded-full ${selected ? 'bg-white/50' : 'bg-gray-300 dark:bg-bg-3'}`}></div>
                    )}
                </>
            )}
            
            {/* Expiry indicator badge - E */}
            {isExpiryDate && !selected && (
                <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full flex items-center justify-center text-[6px] font-bold text-white ${
                    isDiscovered 
                        ? 'bg-purple-500 dark:bg-purple-600/90 shadow-sm shadow-purple-500/20' 
                        : 'bg-amber-400 dark:bg-amber-600/70'
                }`}>
                    E
                </div>
            )}
            
            {/* Rollover indicator badge */}
            {isRolloverDate && !isExpiryDate && !selected && (
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 dark:bg-blue-500/80 rounded-full flex items-center justify-center text-[6px] font-bold text-white">
                    R
                </div>
            )}

            {/* Tooltip — desktop only. It is hover-gated, so on touch it could never
                open; that tier gets the pinned panel below the calendar instead
                (same body via renderDayDetailBody). Skipping it on compact also
                drops its `w-max whitespace-nowrap`, which would otherwise be able
                to push page-level horizontal scroll on a phone. */}
            {status !== DataStatus.FUTURE && !isCompact && (
                <div className={`absolute left-1/2 -translate-x-1/2 hidden group-hover/cell:block z-50 pointer-events-none w-max
                    ${isTopRow ? 'top-full mt-2' : 'bottom-full mb-2'}
                `}>
                    <div className="bg-gray-900 dark:bg-bg-2 text-white text-[10px] p-2 rounded-lg shadow-xl whitespace-nowrap border border-[var(--divider)] z-50">
                        <div className="font-bold mb-1">{dateStr}</div>
                        {renderDayDetailBody(dateStr)}
                    </div>
                    {/* Tooltip Arrow */}
                    <div className={`w-2 h-2 bg-gray-900 dark:bg-bg-2 transform rotate-45 absolute left-1/2 -translate-x-1/2 border-[var(--divider)]
                        ${isTopRow ? '-top-1 border-l border-t' : '-bottom-1 border-r border-b'}
                    `}></div>
                </div>
            )}
        </div>
    );
  };

  const renderTimeline = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const isCalendarView = viewMode === 'calendar';

    return (
      <div className={`${isCalendarView ? '' : 'overflow-x-auto pb-4'}`}>
         <div className={`${isCalendarView ? '' : 'min-w-[800px]'}`}>
             {/* Header Grid - Only Timeline View */}
             {!isCalendarView && (
                 <div className="flex mb-4 border-b border-[var(--divider)] pb-2 pl-16">
                     <div className="flex-1 flex text-[10px] text-fg-3 font-bold justify-between">
                         {Array.from({length: 37}).map((_, i) => (
                             <div key={i} className="flex-1 text-center">{weekDays[i % 7]}</div>
                         ))}
                     </div>
                 </div>
             )}
             
             <div className={`${isCalendarView ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6' : 'space-y-3'}`}>
                {months.map((month, mIdx) => {
                    const firstDayOfMonth = new Date(year, mIdx, 1);
                    let startOffset = firstDayOfMonth.getDay() - 1;
                    if (startOffset === -1) startOffset = 6;
                    const daysInMonth = new Date(year, mIdx+1, 0).getDate();
                    
                    const slotCount = isCalendarView ? 42 : 37;
                    const slots = Array.from({length: slotCount}, (_, i) => {
                         const dayNum = i - startOffset + 1;
                         if (dayNum > 0 && dayNum <= daysInMonth) return dayNum;
                         return null;
                    });
                    
                    const isTopRow = isCalendarView ? mIdx < 4 : mIdx === 0;

                    if (isCalendarView) {
                        // Calendar Card View
                        return (
                            <div key={month} className="bg-bg-3/50 rounded-xl p-3 sm:p-4 border border-[var(--divider)]">
                               <h4 className="text-sm font-bold text-fg-1 mb-3 text-center">{month}</h4>
                               <div className="grid grid-cols-7 gap-1 mb-2">
                                 {weekDays.map((d, i) => (
                                   <div key={i} className="text-[10px] text-center text-fg-3 font-bold">{d}</div>
                                 ))}
                               </div>
                               <div className="grid grid-cols-7 gap-1">
                                 {slots.map((day, idx) => renderDaySlot(day, idx, mIdx, isTopRow, {
                                     cell: 'aspect-square',
                                     empty: 'aspect-square'
                                 }))}
                               </div>
                            </div>
                        );
                    } else {
                        // Timeline View
                        return (
                            <div key={month} className="flex items-center group">
                                <div className="w-16 font-bold text-xs text-fg-1 uppercase tracking-wider">{month}</div>
                                <div className="flex-1 flex items-center justify-between">
                                    {slots.map((day, idx) => renderDaySlot(day, idx, mIdx, isTopRow, {
                                        cell: 'flex-1 h-10',
                                        empty: 'flex-1 h-10'
                                    }))}
                                </div>
                            </div>
                        );
                    }
                })}
             </div>

             {/* Touch tier: the hover tooltip can never open without a pointer, so the
                 tapped day's detail pins here instead — same body, not hover-gated.
                 Dark ground is deliberate: renderDayDetailBody's text is written for
                 the tooltip surface and needs it in both themes. */}
             {isCalendarView && isCompact && focusedDate && (
               <div className="mt-4 rounded-lg border border-[var(--divider)] bg-gray-900 dark:bg-bg-2 p-3 text-[11px] text-white shadow-lg">
                 <div className="flex items-start justify-between gap-3 mb-2">
                   <span className="font-bold">{focusedDate}</span>
                   <button
                     onClick={() => setFocusedDate(null)}
                     className="h-11 w-11 -mt-3 -mr-3 shrink-0 flex items-center justify-center rounded-md text-white/60 hover:text-white transition-colors"
                     aria-label="Close day detail"
                   >
                     <FiX />
                   </button>
                 </div>
                 {renderDayDetailBody(focusedDate)}
               </div>
             )}
         </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Compact Header - Single Line */}
      <div className="bg-bg-2 p-4 rounded-2xl border border-[var(--divider)]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left Section - Back Button + Instrument Details */}
          <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
            {mode !== 'selection' && (
              <button onClick={onBack} className="p-2 hover:bg-bg-3 rounded-lg text-fg-2 transition-colors flex-shrink-0">
                <FiArrowLeft size={20} />
              </button>
            )}
            
            {/* Instrument Name & Exchange */}
            <div className="flex items-center gap-2">
              <h1 className="ds-h1 text-fg-1">{instrument.symbol}</h1>
              <span className="ds-badge sm neutral font-mono">
                {instrument.exchange}
              </span>
            </div>

            {/* Instrument Type - Normalized */}
            {(() => {
                const typeStr = (instrument.type || instrument.instrument_type || 'EQUITY').toUpperCase();
                return (
                    <span className={`ds-badge sm ${
                      typeStr === 'EQUITY' ? 'info' :
                      typeStr.includes('FUT') ? 'role' :
                      typeStr.includes('OPT') ? 'warn' :
                      'neutral'
                    }`}>
                      {typeStr}
                    </span>
                );
            })()}

            {/* Token */}
            <div className="flex items-center gap-1 text-xs">
              <span className="text-fg-2">Token:</span>
              <span className="font-mono font-semibold text-fg-1">{instrument.exchange_token || 'N/A'}</span>
            </div>

            {/* Derivatives Info - Expiry & Strike for Options/Futures */}
            {(instrument.expiry_date || instrument.strike_price) && (
              <div className="flex items-center gap-3 text-xs">
                {instrument.expiry_date && (
                  <div className="flex items-center gap-1">
                    <FiCalendar className="text-fg-3" />
                    <span className="font-semibold text-fg-1">
                      {new Date(instrument.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {instrument.strike_price && (
                  <div className="flex items-center gap-1">
                    <span className="text-fg-2">Strike:</span>
                    <span className="font-semibold text-fg-1">₹{instrument.strike_price}</span>
                  </div>
                )}
                {instrument.option_type && (
                  <span className={`ds-badge sm ${
                    instrument.option_type === 'CE' ? 'profit' : 'loss'
                  }`}>
                    {instrument.option_type === 'CE' ? 'CALL' : 'PUT'}
                  </span>
                )}
              </div>
            )}

            {/* Total Records - Only show if > 0 */}
            {(totalRecordCount > 0) && (
            <div className="hidden md:flex items-center gap-1 text-xs">
              <FiDatabase className="text-fg-3" />
              <span className="font-mono font-semibold text-fg-1">
                {totalRecordCount.toLocaleString()} records
              </span>
            </div>
            )}
          </div>

          {/* Right Section - Intervals + Year + Actions */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-start md:justify-end">
            {/* Interval Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-fg-2">Interval:</span>
              <div className="flex gap-1">
                {['all', '1s', '1m', '5m', '15m', '1h', '1d'].map((timeframe) => (
                  <button
                    key={timeframe}
                    onClick={() => setSelectedTimeframe(timeframe)}
                    className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                      selectedTimeframe === timeframe
                        ? 'bg-accent text-white shadow-md'
                        : 'bg-bg-3 text-fg-2 hover:bg-bg-3 dark:hover:bg-bg-3'
                    }`}
                  >
                    {timeframe.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Timezone Toggle */}
            <button
              onClick={() => setShowExchangeTime(!showExchangeTime)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                showExchangeTime
                  ? 'bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)]'
                  : 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)]'
              }`}
              title={showExchangeTime ? 'Showing Exchange Time - Click to switch to Local Time' : 'Showing Local Time - Click to switch to Exchange Time'}
            >
              <FiGlobe size={12} />
              {showExchangeTime 
                ? `${instrument.exchange} Time` 
                : getUserTimezone().split('/').pop().replace('_', ' ')
              }
            </button>
            {/* Year Selector */}
            <div className="flex bg-bg-3 rounded-lg p-1 items-center">
              <button onClick={() => changeYear('prev')} className="p-1 px-2 hover:bg-white dark:hover:bg-bg-3 rounded transition-colors">
                <FiChevronLeft className="text-fg-2" />
              </button>
              <span className="px-3 text-sm font-bold font-mono text-fg-2 min-w-[60px] text-center">
                {year}
              </span>
              <button onClick={() => changeYear('next')} className="p-1 px-2 hover:bg-white dark:hover:bg-bg-3 rounded transition-colors">
                <FiChevronRight className="text-fg-2" />
              </button>
            </div>
            
            {/* Request Range Button */}
            {selectedRange.start && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDownloadModalOpen(true)}
                  className="btn btn-primary btn-sm flex items-center gap-2"
                >
                  <FiDownload /> Request
                </button>
                {onCreateSimulation && mode !== 'selection' && (
                  <button
                    onClick={() => {
                      const effectiveRollover = continuousFuture
                        ? (rolloverDays === 'default' ? exchangeDefaultRollover : parseInt(rolloverDays, 10))
                        : 0;
                      onCreateSimulation({
                        instrument,
                        start_date: selectedRange.start,
                        end_date: selectedRange.end || selectedRange.start,
                        timeframe: selectedTimeframe === 'all' ? '1m' : selectedTimeframe,
                        is_continuous: !!continuousFuture,
                        rollover_days: effectiveRollover,
                      });
                    }}
                    className="btn btn-secondary btn-sm flex items-center gap-2"
                    title="Create a simulation to replay this data as live ticks"
                  >
                    <FiPlay /> Simulate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Section */}
      {Object.keys(downloads).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {Object.entries(downloads).map(([id, task]) => {
                  const bufferedProgress = Math.max(task.downloadProgress || 0, task.insertProgress || 0);
                  const confirmedProgress = task.insertProgress || 0;

                  return (
                  <motion.div 
                     layout
                     key={id}
                     className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)] shadow-sm"
                  >
                      <div className="flex justify-between items-start mb-2">
                          <div className="min-w-0">
                              <div className="ds-eyebrow text-fg-2 mb-1">Syncing Data</div>
                              <div className="font-mono text-xs text-fg-1 truncate">
                                  {task.range.start} {task.range.end ? `→ ${task.range.end}` : '(Single Day)'}
                              </div>
                              <div className="mt-1 text-[10px] text-fg-2">
                                {task.type} Mode
                              </div>
                          </div>
                          <div className="text-right min-w-[128px] shrink-0">
                            <div className="flex justify-end mb-1">
                              {task.status === 'completed' ? (
                                  <FiCheckCircle className="text-profit" />
                                ) : task.status === 'partial' ? (
                                  <FiAlertCircle className="text-warning" />
                              ) : task.status === 'failed' ? (
                                  <FiAlertCircle className="text-loss" />
                              ) : (
                                  <FiRefreshCw className="animate-spin text-accent" />
                              )}
                            </div>
                            <div className="text-[10px] text-profit font-medium">
                              {task.downloadTotalChunks > 0
                                ? `Chunks ${task.downloadCompletedChunks || 0}/${task.downloadTotalChunks}`
                                : 'Chunks pending'}
                            </div>
                            {(task.downloadedRecords > 0 || task.insertedRecords > 0) && (
                              <div className="text-[10px] text-accent mt-0.5">
                                {`Inserted ${task.insertedRecords || 0}/${task.downloadedRecords || 0} rec`}
                              </div>
                            )}
                          </div>
                      </div>
                        <div className="mb-2">
                          <div className="flex justify-between items-center text-[10px] text-fg-2 mb-1">
                            <span>Progress (buffered/confirmed)</span>
                            <span>
                              {Math.round(confirmedProgress)}% / {Math.round(bufferedProgress)}% • No-data {task.noDataChunks || 0}
                            </span>
                          </div>
                          <div className="relative h-2.5 bg-bg-3 rounded-full overflow-hidden">
                            <motion.div
                              className="absolute inset-y-0 left-0 bg-accent/30"
                              initial={{ width: 0 }}
                              animate={{ width: `${bufferedProgress}%` }}
                            />
                            <motion.div
                              className={`absolute inset-y-0 left-0 ${task.status === 'failed' ? 'bg-loss' : task.status === 'partial' ? 'bg-warning' : 'bg-accent'}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${confirmedProgress}%` }}
                            />
                          </div>
                        </div>

                      {/* Error Message */}
                      {task.status === 'failed' && task.message && (
                          <div className="mt-2 p-2 bg-[var(--badge-loss-bg)] border border-[var(--divider)] rounded-lg">
                              <p className="text-xs text-[var(--badge-loss-fg)] flex items-start gap-1">
                                  <FiAlertCircle className="flex-shrink-0 mt-0.5" />
                                  <span>{task.message}</span>
                              </p>
                          </div>
                      )}
                      {/* Progress Message for in-progress downloads */}
                        {task.status !== 'failed' && task.status !== 'completed' && task.status !== 'partial' && task.message && (
                          <div className="mt-2">
                              <p className="text-xs text-fg-2">{task.message}</p>
                          </div>
                      )}

                      {(task.discovery?.generatedCandidates?.length > 0 || task.discovery?.attempts?.length > 0) && (
                        <div className="mt-2 p-2 rounded-lg border border-[var(--divider)] bg-[var(--badge-accent-bg)]">
                          <div className="text-[10px] font-semibold text-[var(--badge-accent-fg)] mb-1">
                            Expiry Discovery {task.discovery?.monthKey ? `(${task.discovery.monthKey})` : ''}
                          </div>
                          {task.discovery?.generatedCandidates?.length > 0 && (
                            <div className="text-[10px] text-[var(--badge-accent-fg)] mb-1">
                              Candidates: {task.discovery.generatedCandidates.join(', ')}
                            </div>
                          )}
                          {task.discovery?.attempts?.length > 0 && (
                            <div className="text-[10px] text-fg-2 mb-1 flex flex-wrap gap-1">
                              <span className="mr-0.5">Tried:</span>
                              {task.discovery.attempts.map((a, idx) => {
                                const isNoData = a.includes('no_data');
                                const isValid = a.includes('valid') && !a.includes('invalid') && !isNoData;
                                const isError = a.includes('error');
                                let cls = 'text-loss';
                                if (isValid) cls = 'text-profit';
                                else if (isNoData) cls = 'text-warning';
                                else if (isError) cls = 'text-loss';
                                return (
                                  <span key={idx} className={cls}>
                                    {a}{idx < task.discovery.attempts.length - 1 ? ' |' : ''}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {task.discovery?.selectedExpiry && (
                            <div className="text-[10px] font-medium text-profit">
                              Selected: {task.discovery.selectedExpiry}
                            </div>
                          )}
                        </div>
                      )}
                  </motion.div>
              );
            })}
          </div>
      )}

      {/* Timeline View */}
      {/* p-3 below sm: this padding and the month card's both eat into the 7 calendar
          columns, and at 375px they were costing 80px — i.e. ~10px off every day cell.
          sm:p-6 keeps every larger tier (incl. StrategyBuilder's pane) identical. */}
      <div className="bg-bg-2 p-3 sm:p-6 rounded-2xl shadow-sm border border-[var(--divider)]">
          <div className="flex flex-col gap-4 mb-6">
              <h3 className="ds-h3 text-fg-1">Availability Timeline</h3>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                   {Object.entries(DataStatus).map(([key, val]) => (
                       <div key={key} className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded-sm ${statusColors[val]}`}></div>
                           <span className="capitalize text-fg-2">{val}</span>
                       </div>
                   ))}
              </div>
          </div>
          
          {/* Loading State */}
          {loadingCalendar && (
            <div className="flex flex-col items-center justify-center py-12 text-fg-3">
              <FiRefreshCw className="animate-spin text-4xl mb-4" />
              <p>Loading calendar data from MDMS...</p>
            </div>
          )}
          
          {/* Error State */}
          {calendarError && !loadingCalendar && (
            <div className="bg-[var(--badge-loss-bg)] border border-[var(--divider)] rounded-xl p-4">
              <div className="flex items-center gap-2 text-[var(--badge-loss-fg)] mb-2">
                <FiAlertCircle />
                <span className="font-semibold">Error loading calendar</span>
              </div>
              <p className="text-[var(--badge-loss-fg)] text-sm">{calendarError}</p>
              <button
                onClick={fetchCalendarData}
                className="mt-2 text-sm text-[var(--badge-loss-fg)] underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}
          
          {/* Calendar Timeline */}
          {!loadingCalendar && !calendarError && renderTimeline()}
          
          {/* Empty State - No Data */}
          {!loadingCalendar && !calendarError && Object.keys(calendarData).length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-fg-3">
              <FiDatabase className="text-4xl mb-4 opacity-20" />
              <p>No historical data available for this instrument in {year}</p>
              <button
                type="button"
                onClick={() => setDownloadModalOpen(true)}
                className="btn btn-primary btn-sm mt-4"
              >
                Request Historical Data
              </button>
            </div>
          )}
      </div>

      {/* Download Options Modal */}
      <AnimatePresence>
          {downloadModalOpen && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                  <motion.div 
                     initial={{ opacity: 0, scale: 0.95 }}
                     animate={{ opacity: 1, scale: 1 }}
                     exit={{ opacity: 0, scale: 0.95 }}
                     className="bg-bg-2 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
                  >
                      <div className="p-6">
                          <h3 className="ds-h3 mb-4 text-fg-1">Download Options</h3>
                          
                          <div className="space-y-4 mb-6">
                              <div className="bg-bg-3 p-3 rounded-xl">
                                  <div className="text-xs text-fg-2 mb-1">Selected Range</div>
                                  <div className="font-mono font-bold text-fg-1">
                                      {selectedRange.start} 
                                      {selectedRange.end ? (
                                          <> <span className="text-fg-3 mx-2">to</span> {selectedRange.end} </>
                                      ) : (
                                          <span className="text-fg-3 ml-2">(Single Day)</span>
                                      )}
                                  </div>
                              </div>
                              
                              {/* Continuous Future Option - Only show for F&O instruments with ranges crossing expiry */}
                              {(() => {
                                  // Robust F&O detection - check multiple fields
                                  const instType = (instrument?.instrument_type || instrument?.type || instrument?.product_type || '').toUpperCase();
                                  const isFnO = ['FUTURE', 'OPTION', 'FUT', 'OPT', 'FUTURES', 'OPTIONS'].includes(instType);
                                  // Also check if it's an F&O exchange or has expiry (extra signals)
                                  const isFnOExchange = ['NFO', 'MCX', 'CDS', 'BFO'].includes((instrument?.exchange || '').toUpperCase());
                                  const hasExpiry = !!instrument?.expiry_date || !!instrument?.expiry;
                                  const likelyFnO = isFnO || (isFnOExchange && hasExpiry);
                                  
                                  const startDate = selectedRange.start ? new Date(selectedRange.start) : null;
                                  const endDate = selectedRange.end ? new Date(selectedRange.end) : startDate;
                                  const daysDiff = startDate && endDate ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) : 0;
                                  
                                  // Check if range crosses any expiry date (the correct way to determine multi-contract)
                                  const crossedExpiries = expiryDates.filter(expStr => {
                                      const expDate = new Date(expStr);
                                      return startDate && endDate && expDate > startDate && expDate <= endDate;
                                  });
                                  const crossesExpiry = crossedExpiries.length > 0;
                                  
                                  // Show toggle for all F&O instruments to allow multi-expiry stitching/rolling
                                  const shouldShowToggle = likelyFnO;
                                  
                                  if (shouldShowToggle) {
                                      return (
                                          <div className="bg-bg-3/50 p-4 rounded-xl border border-[var(--divider)]">
                                              <div className="flex items-center justify-between">
                                                  <div className="flex-1">
                                                      <div className="flex items-center gap-2 mb-1">
                                                          <span className="text-accent">
                                                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                  <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 5.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 11H14a1 1 0 100-2H8.414l1.293-1.293z" clipRule="evenodd" />
                                                              </svg>
                                                          </span>
                                                          <span className="font-semibold text-sm text-fg-1">Continuous Contract Mode</span>
                                                      </div>
                                                      <p className="text-xs text-fg-2 leading-relaxed">
                                                          {crossesExpiry ? (
                                                              <>Your range crosses <span className="font-medium text-warning">{crossedExpiries.length} expiry{crossedExpiries.length > 1 ? ' dates' : ''}</span> ({crossedExpiries[0]}) — enable to auto-stitch from correct contracts.</>
                                                          ) : (
                                                              <>Enable to automatically fetch data from the valid contract for each date (handles rollover/expiry).</>
                                                          )}
                                                      </p>
                                                  </div>
                                                  <button
                                                      type="button"
                                                      onClick={() => setContinuousFuture(!continuousFuture)}
                                                      className={`relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg-2 ${
                                                          continuousFuture
                                                              ? 'bg-accent'
                                                              : 'bg-bg-3'
                                                      }`}
                                                  >
                                                      <span
                                                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                              continuousFuture ? 'translate-x-5' : 'translate-x-0'
                                                          }`}
                                                      />
                                                  </button>
                                              </div>
                                              
                                              {/* Rollover Days Selector - shown when continuous mode is enabled */}
                                              {continuousFuture && (
                                                  <div className="mt-3 pt-3 border-t border-[var(--divider)]">
                                                      <div className="flex items-center justify-between">
                                                          <div className="flex-1">
                                                              <div className="flex items-center gap-2 mb-1">
                                                                  <span className="text-accent">
                                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                      </svg>
                                                                  </span>
                                                                  <span className="text-xs font-medium text-fg-2">Rollover Days Before Expiry</span>
                                                              </div>
                                                              <p className="text-[10px] text-fg-2">
                                                                  Switch to next contract N trading days before expiry
                                                              </p>
                                                          </div>
                                                          <select
                                                              value={rolloverDays}
                                                              onChange={(e) => setRolloverDays(e.target.value)}
                                                              className="ml-4 ds-input"
                                                          >
                                                              <option value="default">Exchange Default ({exchangeDefaultRollover})</option>
                                                              <option value="0">0 - Use until expiry</option>
                                                              <option value="1">1 Trading Day</option>
                                                              <option value="2">2 Trading Days</option>
                                                              <option value="3">3 Trading Days</option>
                                                              <option value="4">4 Trading Days</option>
                                                              <option value="5">5 Trading Days</option>
                                                          </select>
                                                      </div>
                                                  </div>
                                              )}
                                          </div>
                                      );
                                  }
                                  return null;
                              })()}
                          </div>
                          
                          {/* Override Contract End Date — F&O only */}
                          {(() => {
                              const instType = (instrument?.instrument_type || instrument?.type || '').toUpperCase();
                              const isFnO = ['FUTURE', 'OPTION', 'FUT', 'OPT', 'FUTURES', 'OPTIONS'].includes(instType);
                              const isFnOExchange = ['NFO', 'MCX', 'CDS', 'BFO'].includes((instrument?.exchange || '').toUpperCase());
                              const hasExpiry = !!instrument?.expiry_date || !!instrument?.expiry;
                              if (!(isFnO || (isFnOExchange && hasExpiry))) return null;
                              
                              return (
                                  <div className="bg-[var(--badge-warn-bg)] p-4 rounded-xl border border-[var(--divider)]">
                                      <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-1">
                                                  <FiCalendar className="text-warning w-4 h-4" />
                                                  <span className="font-semibold text-sm text-fg-1">Override Contract End Date</span>
                                              </div>
                                              <p className="text-xs text-fg-2 leading-relaxed">
                                                  Manually specify the contract expiry if auto-detection is incorrect for any month.
                                              </p>
                                          </div>
                                          <button
                                              type="button"
                                              onClick={() => {
                                                  setExpiryOverrideEnabled(!expiryOverrideEnabled);
                                                  if (expiryOverrideEnabled) setExpiryOverrideDate('');
                                              }}
                                              className={`relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2 focus:ring-offset-bg-2 ${
                                                  expiryOverrideEnabled
                                                      ? 'bg-warning'
                                                      : 'bg-bg-3'
                                              }`}
                                          >
                                              <span
                                                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                      expiryOverrideEnabled ? 'translate-x-5' : 'translate-x-0'
                                                  }`}
                                              />
                                          </button>
                                      </div>
                                      
                                      {/* Date picker when enabled */}
                                      {expiryOverrideEnabled && (
                                          <div className="mt-3 pt-3 border-t border-[var(--divider)]">
                                              <label className="block text-xs font-medium text-fg-2 mb-1.5">
                                                  Contract End Date (Expiry)
                                              </label>
                                              <input
                                                  type="date"
                                                  value={expiryOverrideDate}
                                                  onChange={(e) => setExpiryOverrideDate(e.target.value)}
                                                  className="w-full ds-input"
                                              />
                                              <p className="text-[10px] text-warning mt-1.5">
                                                  This date will be used as the contract expiry for the selected download range instead of auto-detected expiry.
                                              </p>
                                          </div>
                                      )}
                                  </div>
                              );
                          })()}
                          
                          {/* Broker Selector - Only show when multiple options available */}
                          {brokerOptions?.has_multiple_options && (
                              <div className="bg-bg-3 p-4 rounded-xl border border-[var(--divider)]">
                                  <div className="flex items-center gap-2 mb-3">
                                      <FiDatabase className="text-accent" size={16} />
                                      <span className="font-semibold text-sm text-fg-1">Download via Broker</span>
                                  </div>
                                  <div className="space-y-2">
                                      {brokerOptions.available_brokers.map((broker) => (
                                          <label 
                                              key={broker.broker_id}
                                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                                  selectedBrokerId === broker.broker_id
                                                      ? 'bg-accent/10 border border-accent/30'
                                                      : 'hover:bg-bg-3/50'
                                              }`}
                                          >
                                              <input
                                                  type="radio"
                                                  name="broker"
                                                  value={broker.broker_id}
                                                  checked={selectedBrokerId === broker.broker_id}
                                                  onChange={() => setSelectedBrokerId(broker.broker_id)}
                                                  className="w-4 h-4 text-accent border-[var(--divider)] focus:ring-accent"
                                              />
                                              <span className="text-sm font-medium text-fg-1">
                                                  {broker.broker_name}
                                              </span>
                                              {brokerOptions.recommended_broker_id === broker.broker_id && (
                                                  <span className="ds-badge xs profit ml-auto">
                                                      Recommended
                                                  </span>
                                              )}
                                          </label>
                                      ))}
                                  </div>
                              </div>
                          )}
                          
                          <div className="flex gap-3">
                              <button
                                 type="button"
                                 onClick={() => setDownloadModalOpen(false)}
                                 className="btn btn-secondary flex-1"
                              >
                                  Cancel
                              </button>
                              <button
                                 type="button"
                                 onClick={startDownload}
                                 className="btn btn-primary flex-1"
                              >
                                  Start Download
                              </button>
                          </div>
                      </div>
                  </motion.div>
              </div>
          )}
      </AnimatePresence>

      {/* DeeTrade Service Modal - Brokers Don't Support Historical Data */}
      <AnimatePresence>
          {deetradeServiceModal && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                  <motion.div 
                     initial={{ opacity: 0, scale: 0.95, y: 20 }}
                     animate={{ opacity: 1, scale: 1, y: 0 }}
                     exit={{ opacity: 0, scale: 0.95, y: 20 }}
                     className="bg-bg-2 w-full max-w-lg rounded-3xl shadow-2xl border border-[var(--divider)] overflow-hidden max-h-[90vh] overflow-y-auto"
                  >
                      {/* Premium Header — deliberately dark in BOTH themes (branded hero),
                          so everything inside it is light-on-dark by hand, not tokens. */}
                      <div className="relative min-h-32 bg-slate-900 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-purple-600/20" />
                          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.4),transparent_70%)]" />
                          
                          <div className="relative h-full p-5 sm:p-8 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center backdrop-blur-xl">
                                      <FiShield className="text-2xl sm:text-3xl text-indigo-400" />
                                  </div>
                                  <div className="min-w-0">
                                      <h3 className="ds-h3 text-white tracking-tight">Request Secured</h3>
                                      <div className="flex items-center gap-2 mt-1">
                                          <span className="flex h-2 w-2 shrink-0 rounded-full bg-emerald-500 animate-pulse" />
                                          <span className="text-slate-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider">DeeTrade Priority Service</span>
                                      </div>
                                  </div>
                              </div>
                              <button 
                                  onClick={() => setDeetradeServiceModal(null)}
                                  className="p-2 hover:bg-white/10 rounded-full transition-all text-slate-400 hover:text-white"
                              >
                                  <FiX className="text-2xl" />
                              </button>
                          </div>
                      </div>

                      {/* Content Body */}
                      <div className="p-8 space-y-6">
                          {/* Broker Status Section */}
                          <div className="flex items-start gap-4 p-4 rounded-2xl bg-bg-3 dark:bg-bg-2/50 border border-[var(--divider)]">
                              <div className="mt-1">
                                  <FiAlertCircle className="text-warning text-xl" />
                              </div>
                              <div>
                                  <h4 className="text-sm font-bold text-fg-1">Broker Limitation Detected</h4>
                                  <p className="text-xs text-fg-2 mt-1 leading-relaxed">
                                      {deetradeServiceModal.brokers.length > 0 
                                          ? `Your connected brokers (${deetradeServiceModal.brokers.join(', ')}) do not currently provide historical data APIs.`
                                          : 'Your current broker configuration does not support direct historical data retrieval.'}
                                  </p>
                              </div>
                          </div>

                          {/* DeeTrade Facilitation Promise */}
                          {/* Accent-filled promo card — its contents are intentionally
                              light-on-accent, so they stay hand-written white, not tokens. */}
                          <div className="relative p-6 rounded-2xl bg-accent text-white shadow-xl shadow-accent/20 overflow-hidden">
                              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                              <div className="relative flex items-start gap-4">
                                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
                                      <FiZap className="text-xl text-white" />
                                  </div>
                                  <div>
                                      <h4 className="ds-h4">We've Got You Covered</h4>
                                      <p className="text-sm text-white/85 mt-1 leading-relaxed">
                                          {deetradeServiceModal.message}
                                      </p>
                                      <div className="flex items-center gap-2 mt-3 px-3 py-1.5 bg-white/10 rounded-full w-fit">
                                          <FiCheckCircle className="text-xs text-white/85" />
                                          <span className="text-[10px] font-bold uppercase tracking-widest text-white/85">Complimentary Service</span>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* Request Summary Grid */}
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="p-4 rounded-2xl border border-[var(--divider)] bg-bg-2">
                                  <span className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">Instrument</span>
                                  <div className="flex items-center gap-2 mt-1">
                                      <FiActivity className="text-accent text-xs" />
                                      <p className="text-sm font-bold text-fg-1">
                                          {deetradeServiceModal.requestDetails?.instrument}
                                      </p>
                                  </div>
                              </div>
                              <div className="p-4 rounded-2xl border border-[var(--divider)] bg-bg-2">
                                  <span className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">Exchange</span>
                                  <p className="text-sm font-bold text-fg-1 mt-1">
                                      {deetradeServiceModal.requestDetails?.exchange}
                                  </p>
                              </div>
                              <div className="sm:col-span-2 p-4 rounded-2xl border border-[var(--divider)] bg-bg-2">
                                  <span className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">Date Range</span>
                                  <div className="flex items-center gap-2 mt-1">
                                      <FiCalendar className="text-accent text-xs" />
                                      <p className="text-sm font-bold text-fg-1">
                                          {deetradeServiceModal.requestDetails?.date_range}
                                      </p>
                                  </div>
                              </div>
                          </div>

                          {/* Timeline Info */}
                          <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-bg-3 dark:bg-bg-2/30 border border-dashed border-[var(--divider)]">
                              <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-8 h-8 shrink-0 rounded-full bg-bg-2 flex items-center justify-center shadow-sm">
                                      <FiClock className="text-accent" />
                                  </div>
                                  <span className="text-sm font-medium text-fg-2">Estimated Delivery</span>
                              </div>
                              <span className="text-sm font-bold text-fg-1">
                                  {deetradeServiceModal.estimatedTime}
                              </span>
                          </div>
                      </div>

                      {/* Action Footer */}
                      <div className="p-5 sm:p-8 pt-0 sm:pt-0">
                          <button 
                              onClick={() => setDeetradeServiceModal(null)}
                              className="group relative w-full py-4 rounded-2xl font-bold bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-900/20 dark:shadow-white/10 overflow-hidden"
                          >
                              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                              <span className="relative">Acknowledge & Continue</span>
                          </button>
                          {deetradeServiceModal.requestId && (
                              <p className="text-center text-[10px] text-fg-3 mt-4 font-medium uppercase tracking-widest">
                                  Reference ID: <span className="text-fg-2">#{deetradeServiceModal.requestId}</span>
                              </p>
                          )}
                      </div>
                  </motion.div>
              </div>
          )}
      </AnimatePresence>

    </div>
  );
};

export default HistoricalDataTimeline;
